// ForgotPassword.jsx - Password reset page
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUser } from '../context/UserContext';
import { localePath, parseLocalePath } from '../constants/Routes';
import AuthLayout from '../components/auth/AuthLayout';
import { AUTH_LINK_CLASS } from '../components/auth/authLinkStyles';
import AuthHeader from '../components/auth/AuthHeader';
import TextField from '../components/auth/TextField';
import PrimaryButton from '../components/auth/PrimaryButton';
import { ArrowLeft } from 'lucide-react';

// Simple PageTitle component
const PageTitle = ({ title }) => {
  React.useEffect(() => {
    document.title = title || 'Bloom IQ';
  }, [title]);
  return null;
};

const ForgotPassword = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { locale: lang } = parseLocalePath(location.pathname);
  const { sendPasswordResetEmail } = useUser();
  const loginHref = localePath(lang, '/login');

  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Validate email
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!email.trim()) {
      setError(t('errors.enterEmail'));
      return;
    }

    if (!validateEmail(email)) {
      setError(t('errors.enterValidEmail'));
      return;
    }

    setLoading(true);

    try {
      await sendPasswordResetEmail(email);
      setSuccess(true);
    } catch (error) {
      console.error('Password reset error:', error);

      // Handle Firebase auth errors
      if (error.code === 'auth/user-not-found') {
        setError(t('errors.noAccountFound'));
      } else if (error.code === 'auth/invalid-email') {
        setError(t('errors.invalidEmail'));
      } else if (error.code === 'auth/too-many-requests') {
        setError(t('errors.tooManyRequests'));
      } else {
        setError(error.message || t('errors.failedResetEmail'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageTitle title={`${t('auth.resetPassword')} - Bloom IQ`} />
      <AuthLayout variant="light">
        <div className="w-full">
          {/* Back Button */}
          <Link
            to={loginHref}
            className={`mb-6 inline-flex items-center ${AUTH_LINK_CLASS}`}
          >
            <ArrowLeft size={20} className="mr-2" />
            <span>{t('auth.backToLogin')}</span>
          </Link>

          {/* Logo and Title */}
          <AuthHeader
            title={t('auth.resetPassword')}
            subtitle={t('auth.resetPasswordSubtitle')}
          />

          {/* Success message */}
          {success ? (
            <div className="space-y-6">
              <div className="bg-[#E7F6EE] border border-[#DCE7DF] text-[#15803D] p-4 rounded-md text-sm">
                <p className="font-medium mb-2">{t('auth.checkEmail')}</p>
                <p>
                  {t('auth.resetEmailSent')} <strong>{email}</strong>
                </p>
                <p className="mt-2">
                  {t('auth.checkSpam')}
                </p>
              </div>

              <Link to={loginHref}>
                <PrimaryButton type="button">{t('auth.returnToLogin')}</PrimaryButton>
              </Link>

              <div className="text-center">
                <button
                  onClick={() => {
                    setSuccess(false);
                    setEmail('');
                  }}
                  className={`text-sm ${AUTH_LINK_CLASS}`}
                >
                  {t('auth.tryDifferentEmail')}
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Error message */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md mb-5 text-sm">
                  {error}
                </div>
              )}

              {/* Reset Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                <TextField
                  variant="light"
                  label={t('auth.emailAddress')}
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError('');
                  }}
                  error={error && !email ? error : ''}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                  autoFocus
                />

                {/* Submit Button */}
                <PrimaryButton type="submit" loading={loading} disabled={loading}>
                  {t('auth.sendResetLink')}
                </PrimaryButton>
              </form>

              {/* Additional info */}
              <div className="mt-6 text-center text-sm text-[#5d625f] font-['Inter']">
                <p>{t('auth.rememberPassword')}</p>
                <Link
                  to={loginHref}
                  className={`mt-1 inline-block ${AUTH_LINK_CLASS}`}
                >
                  {t('auth.backToLogin')}
                </Link>
              </div>
            </>
          )}
        </div>
      </AuthLayout>
    </>
  );
};

export default ForgotPassword;
